import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { ErrorState } from '../Layout/ErrorState';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ChatListErrorBoundaryBase extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ChatList Error Boundary caught an error:', error, errorInfo);
    
    // Report to analytics/monitoring service
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: `ChatList Error: ${error.message}`,
        fatal: false,
        error_boundary: 'ChatListErrorBoundary'
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorState
          compact
          eyebrow="Chat history"
          title="Unable to load chats"
          message="Your conversations are still safe. Try reloading the list."
          primaryAction={{ label: 'Try again', onClick: this.handleRetry }}
          supportHref=""
          error={this.state.error}
        />
      );
    }

    return this.props.children;
  }
}

// Wrapper component to use hooks
const ChatListErrorBoundary: React.FC<Props> = ({ children, fallback }) => {
  const toast = useToast();

  React.useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('chat')) {
        toast('Failed to load chats. Please try refreshing the page.', 'error');
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, [toast]);

  return (
    <ChatListErrorBoundaryBase fallback={fallback}>
      {children}
    </ChatListErrorBoundaryBase>
  );
};

export default ChatListErrorBoundary;
